import { useState } from 'react';
import { Link } from 'react-router-dom';
import { forumApi } from '../../api/endpoints.js';
import { useAuth } from '../../state/AuthContext.jsx';
import { useNotice } from '../../state/NoticeContext.jsx';
import {
  Button,
  Card,
  Chips,
  Empty,
  Muted,
  Select,
  Spinner,
  Text,
  fmtDate,
  useAsync
} from '../../ui.jsx';

export default function Forums() {
  const { user } = useAuth();
  const notice = useNotice();
  const [category, setCategory] = useState('All');
  const { data, loading, error, reload } = useAsync(() => forumApi.list({ category }), [category]);

  const [form, setForm] = useState({ title: '', body: '', category: 'General', school: 'All training colleges' });
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);

  if (loading) return <Spinner label="Loading discussions" />;
  if (error) return <div className="note">You need to be signed in to read the forums.</div>;

  const threads = data?.threads || [];
  const categories = data?.categories || [];

  const ask = async (event) => {
    event.preventDefault();
    setBusy(true);
    try {
      await forumApi.create({
        title: form.title.trim(),
        body: form.body.trim(),
        category: form.category,
        school: form.school.trim() || 'All training colleges'
      });
      notice.success('Your question is posted.');
      setForm({ title: '', body: '', category: form.category, school: 'All training colleges' });
      setAsking(false);
      reload();
    } catch (error) {
      notice.fromError(error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <h2>Forums</h2>
      <Muted>Ask about admissions, hostels and scholarships. Students already on campus answer.</Muted>

      <Card>
        <Chips
          label="Category"
          options={['All', ...categories]}
          value={category}
          onChange={setCategory}
        />
      </Card>

      {user ? (
        <Card>
          <div className="row sp">
            <b>Ask a question</b>
            <Button size="sm" onClick={() => setAsking((a) => !a)}>{asking ? 'Cancel' : 'New question'}</Button>
          </div>
          {asking ? (
            <form onSubmit={ask} style={{ marginTop: 14 }}>
              <Text
                label="Title"
                required
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
              />
              <div style={{ height: 12 }} />
              <label>
                Question
                <textarea
                  rows={5}
                  required
                  value={form.body}
                  onChange={(e) => setForm({ ...form, body: e.target.value })}
                />
              </label>
              <div className="row" style={{ marginTop: 12, alignItems: 'flex-end' }}>
                <div style={{ flex: 1 }}>
                  <Select
                    label="Category"
                    value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                  >
                    {categories.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </Select>
                </div>
                <div style={{ flex: 1 }}>
                  <Text
                    label="School"
                    hint="Optional."
                    value={form.school}
                    onChange={(e) => setForm({ ...form, school: e.target.value })}
                  />
                </div>
              </div>
              <div style={{ marginTop: 14 }}>
                <Button type="submit" busy={busy}>Post question</Button>
              </div>
            </form>
          ) : null}
        </Card>
      ) : (
        <Card>
          <Muted>
            <Link to="/signin">Sign in</Link> to ask a question or reply. Reading needs an account too,
            so discussions stay useful.
          </Muted>
        </Card>
      )}

      {threads.length === 0 ? (
        <Empty title="No questions here yet">
          Be the first to ask. Other applicants will see it immediately.
        </Empty>
      ) : (
        <Card>
          {threads.map((thread) => (
            <div className="prog" key={thread.id}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <b>{thread.title}</b>
                <div className="mu">
                  {thread.category} · {thread.school} · asked by {thread.author || 'Entrance'} ·{' '}
                  {fmtDate(thread.createdAt)}
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div className="mu">{thread.replyCount} {thread.replyCount === 1 ? 'reply' : 'replies'}</div>
                <div className="mu">{thread.votes} {thread.votes === 1 ? 'vote' : 'votes'}</div>
                <div style={{ marginTop: 8 }}>
                  <Link to={`/forums/${thread.id}`} className="btn sm" style={{ textDecoration: 'none' }}>
                    Open
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </Card>
      )}
    </>
  );
}
